"use client";

import React, { useState, CSSProperties } from "react";
import { theme } from "@/styles/theme";

type PasswordPanelProps = {
  projectName: string;
  projectHref: string; // kept for clarity / future use – navigation is handled by the parent
  /**
   * SHA-256 hash of the correct code (hex string, lowercase).
   * If undefined, the panel will show a configuration error on submit.
   */
  codeHash?: string;
  /** Called when the code is verified as correct */
  onSuccess: () => void;
};

async function hashCodeSHA256(code: string): Promise<string | null> {
  if (typeof window === "undefined" || !window.crypto?.subtle) return null;

  const encoder = new TextEncoder();
  const data = encoder.encode(code);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  const bytes = new Uint8Array(digest);

  // Normalize to lowercase hex to match how you generated hashes in Node
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toLowerCase();
}

export function PasswordPanel(props: PasswordPanelProps) {
  const { projectName, codeHash, onSuccess } = props;

  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [focused, setFocused] = useState(false);

  const disabled = checking || !codeHash;

  const buildInputStyle = (): CSSProperties => {
    const hasError = !!error;
    const isFocused = focused;

    let border: string | undefined;
    if (hasError) border = "1px solid #FF0031";
    else if (isFocused) border = `1px solid ${theme.colors.Green}`;

    return {
      display: "flex",
      height: 30,
      padding: "4px 8px",
      alignItems: "center",
      gap: 8,
      alignSelf: "stretch",
      width: "100%",
      background: theme.colors.DarkGray,
      color: disabled ? theme.colors.Gray : theme.colors.White,
      boxShadow:
        "-1px -1px 0 0 #FFF inset, 1px 1px 0 0 #808080 inset, -2px -2px 0 0 #C1C1C1 inset, 2px 2px 0 0 #000 inset",
      border: border ?? "none",
      outline: "none",
      fontFamily: theme.fonts.body,
      fontSize: 14,
      boxSizing: "border-box",
    };
  };

  const handleAccept = async () => {
    const trimmed = value.trim();

    if (!trimmed) {
      setError("Code required.");
      return;
    }

    if (!codeHash) {
      // Safer to deny if misconfigured on the frontend
      setError("Project is not configured for access.");
      return;
    }

    setChecking(true);
    setError(null);

    try {
      const hash = await hashCodeSHA256(trimmed);
      if (!hash) {
        setError("Your browser cannot verify the code.");
        return;
      }

      if (hash !== codeHash.toLowerCase()) {
        setError("Incorrect code.");
        return;
      }

      // Correct code – delegate navigation to parent
      onSuccess();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        fontFamily: theme.fonts.body,
        color: theme.colors.White,
        height: "100%",
      }}
    >
      {/* Project name hint / title */}
      <div
        style={{
          fontSize: 15,
          color: theme.colors.Text,
          marginBottom: 5,
        }}
      >
        Enter access code for
        <span
          style={{
            marginLeft: 6,
            fontWeight: 700,
            color: theme.colors.Green,
          }}
        >
          {projectName}
        </span>
        .
      </div>

      {/* Input field */}
      <input
        type="password"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Enter code"
        autoFocus
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void handleAccept();
          }
        }}
        style={buildInputStyle()}
      />

      {/* Error area – fixed height so layout does not shift */}
      <div
        style={{
          marginTop: 5,
          minHeight: 16, // reserve space for one line of text
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        <span
          style={{
            fontSize: 12,
            color: "#FF0031",
            visibility: error ? "visible" : "hidden",
          }}
        >
          {error || "placeholder"}
        </span>
      </div>

      {/* Spacer to push button to bottom */}
      <div style={{ flex: 1 }} />

      {/* Accept button aligned to the right – Win95 primary style */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => void handleAccept()}
          style={{
            minWidth: 200,
            height: 36,
            background: disabled ? theme.colors.DarkAlt : theme.colors.Dark,
            border: disabled
              ? `1px solid ${theme.colors.MidGray}`
              : `1px solid ${theme.colors.White}`,
            color: disabled ? theme.colors.Gray : theme.colors.White,
            boxShadow: disabled
              ? `inset -2px -2px 0 rgba(127,127,127,0.5), inset 2px 2px 0 ${theme.colors.MidGray}`
              : "-3px -3px 0 0 rgba(127, 127, 127, 0.50) inset, 2px 2px 0 0 " +
                theme.colors.LightGray +
                " inset",
            fontFamily: theme.fonts.body,
            fontSize: 15,
            cursor: disabled ? "default" : "pointer",
            outline: "none",
            boxSizing: "border-box",
            opacity: checking ? 0.75 : 1,
          }}
        >
          {checking ? "Checking..." : "Accept"}
        </button>
      </div>
    </div>
  );
}
